import {getGeocode, getLatLng,} from "use-places-autocomplete";
import { calculateETAs } from '../calculations/calculateETAs';
import Axios from 'axios'

/**
 * optimizeInspections.js
 * 
 * This file contains functions to transform inspection assignments into a format
 * suitable for route optimization. It takes the assignments data from SelectionDashboard.js
 * and converts it into a format that can be used with a route optimization API.
 */

/**
 * Checks if lunch break (11:00 AM - 1:00 PM) is already covered by prescheduled inspections
 * 
 * @param {Array} inspections - Array of inspections to check
 * @returns {boolean} - True if lunch break is already covered, false if we should add one
 */
function isLunchBreakAlreadyCovered(inspections) {
  // Filter to only prescheduled inspections
  const prescheduledInspections = inspections.filter(inspection => inspection.Status === "Scheduled");
  
  if (prescheduledInspections.length === 0) {
    // No prescheduled inspections, so we need to add lunch break
    return false;
  }
  
  // Check if any prescheduled inspection completely covers the lunch period (11:00 AM - 1:00 PM)
  for (const inspection of prescheduledInspections) {
    if (inspection.AppointmentStart && inspection.AppointmentFinish) {
      const startTime = new Date(inspection.AppointmentStart);
      const endTime = new Date(inspection.AppointmentFinish);
      
      // Extract time components
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const endHour = endTime.getHours();
      const endMinute = endTime.getMinutes();
      
      // Convert to minutes for easier comparison
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      
      // Lunch period: 11:00 AM (660 minutes) to 1:00 PM (780 minutes)
      const lunchStartMinutes = 11 * 60; // 11:00 AM
      const lunchEndMinutes = 13 * 60;   // 1:00 PM
      
      // Check if this inspection completely covers the lunch period
      if (startMinutes <= lunchStartMinutes && endMinutes >= lunchEndMinutes) {
        // This inspection completely covers the lunch period
        console.log(`Lunch break already covered by inspection ${inspection.INSPECTION_ID} (${inspection.AppointmentStart} - ${inspection.AppointmentFinish})`);
        return true;
      }
    }
  }
  
  // No prescheduled inspection completely covers lunch period
  return false;
}

/**
 * Transforms the assignments data into a format suitable for route optimization
 * 
 * @param {Array} assignments - Array of objects containing assignedInspector and assignedInspections
 * @param {Date} selectedDate - The selected date for optimization
 * @param {Function} onProgress - Optional callback function to report progress (currentRoute, totalRoutes)
 * @returns {Promise<Array>} - Array of optimized assignments with route information
 */
export async function optimizeInspectorRoutes(assignments, selectedDate, onProgress = null) {
  if (!assignments || !Array.isArray(assignments)) {
    console.warn('Invalid assignments data provided to optimizeInspectorRoutes');
    return { optimizedAssignments: [], failedAssignments: [] };
  }

  const optimizedAssignments = [];
  const failedAssignments = [];
  
  // Process each assignment
  for (let i = 0; i < assignments.length; i++) {
    const { assignedInspector, assignedInspections: originalInspections } = assignments[i];
    
    // Skip if no inspections assigned
    if (!originalInspections || originalInspections.length === 0) {
      continue;
    }

    // Report progress
    if (onProgress) {
      onProgress(i + 1, assignments.length, assignedInspector.INSPECTOR_ID);
    }

    // Check to see if there are any prescheduled inspections
    // If there are, we will optimize this route differently and return here
    const hasPrescheduledInspections = originalInspections.some(inspection => inspection.Status === "Scheduled");
    if (hasPrescheduledInspections) {
      console.log(`➡️ Optimizing route ${i+1} of ${assignments.length} [contains prescheduled])`);

      // Create the inspector's address string
      const inspectorAddress = formatAddress(
        assignedInspector.Street,
        assignedInspector.City,
        assignedInspector.State,
        assignedInspector.Postcode
      );

      let inspectorCoordinates = await getCoordinates(inspectorAddress);
      if (!inspectorCoordinates) {
        console.warn(`Cannot retrieve inspector coordinates for ${assignedInspector.INSPECTOR_ID}`);
        failedAssignments.push({
          inspector: assignedInspector,
          inspections: originalInspections,
          reason: 'Cannot retrieve inspector coordinates',
          params: null,
          selectedDate: selectedDate
        });
        continue;
      }
      
      // Format the date string from selectedDate
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      let startTime = `${dateStr}T08:00:00`;
      let endTime = `${dateStr}T16:30:00`;

      // Check for specific time window conditions
      const hasEarlyStartInspection = originalInspections.some(inspection => {
        if (inspection.AppointmentStart && inspection.AppointmentStart.trim()) {
          const timeOnly = inspection.AppointmentStart.split(' ')[1];
          return timeOnly === "08:00:00";
        }
        return false;
      });
      const hasLateEndInspection = originalInspections.some(inspection => {
        if (inspection.AppointmentFinish && inspection.AppointmentFinish.trim()) {
          const timeOnly = inspection.AppointmentFinish.split(' ')[1];
          return timeOnly === "16:30:00";
        }
        return false;
      });

      // If any inspection starts at 8:00, extend start time by 30 min
      if (hasEarlyStartInspection) {
        startTime = `${dateStr}T07:30:00`;
        console.log("Early start inspection detected - extending start time to 07:30:00");
      }
      // If any inspection ends at 16:30, extend 30 min
      if (hasLateEndInspection) {
        endTime = `${dateStr}T17:30:00`;
        console.log("Late end inspection detected - extending end time to 17:00:00");
      }
      // this is temporary, they said theyll change this process - 8-16:30 or 1 inspection
      if ((hasEarlyStartInspection && hasLateEndInspection) || originalInspections.length === 1) {
        startTime = `${dateStr}T00:00:00`;
        endTime = `${dateStr}T23:59:59`;
        console.log("Actually early start and late end inspection detected (or 1 inspection) - extending start time to 00:00:00 and end time to 23:59:59");
      }

      // Create a working copy of inspections that we can modify
      let currentInspections = [...originalInspections];
      let prescheduledOptimizedRoute = null;
      let routeParams = null; // Declare at higher scope for failedAssignments

      let iterationCount = 0;
      const maxIterations = 5;
      
      // Keep trying until we get a valid route or run out of non-prescheduled inspections
      while (currentInspections.length > 0) {

        // Add maximum iteration limit
        if (iterationCount++ > maxIterations) {
          console.error("Maximum iterations reached - breaking loop");
          prescheduledOptimizedRoute = null;
          break;
        }

        try {

          // Create the stop list from current inspections
          const stopList = currentInspections.map(inspection => {
            let getDuration;
            if (inspection.Status === "Scheduled") {
              // Calculate duration from appointment times for scheduled inspections
              getDuration = parseInt(inspection.DURATION, 10); // method 1
              if (!getDuration) { // method 2
                const startTime = new Date(inspection.AppointmentStart);
                const endTime = new Date(inspection.AppointmentFinish);
                getDuration = Math.round((endTime - startTime) / 1000);
              }
              if (!getDuration) {
                console.warn(`No duration found for scheduled inspection ${inspection.INSPECTION_ID}`);
                getDuration = 0;
              }
            } else {
              getDuration = inspection.importedDuration;
            }
            
            const baseStop = {
              address: formatAddress(
                inspection.Street,
                inspection.City,
                inspection.State,
                inspection.Postcode
              ),
              coordinates: [inspection.lat, inspection.lng],
              duration: getDuration,
            };

            if (inspection.Status === "Scheduled") {
              const startISO = inspection.AppointmentStart.replace(' ', 'T');
              
              // Parse start time and add 10 minutes while handling hour rollover
              const [datePart, timePart] = inspection.AppointmentStart.split(' ');
              const [hours, minutes, seconds] = timePart.split(':');
              
              // Create a Date object to handle minute/hour arithmetic
              const startDate = new Date(`${datePart}T${timePart}`);
              const endDate = new Date(startDate.getTime() + 10 * 60000); // Add 10 minutes in milliseconds
              
              // Format the end time back to ISO string and extract just the time portion
              const endISO = `${datePart}T${endDate.getHours().toString().padStart(2,'0')}:${endDate.getMinutes().toString().padStart(2,'0')}:${seconds}`;
              
              return {
                ...baseStop,
                timeWindow: {
                  start: startISO,
                  end: endISO
                }
              };
            }

            return baseStop;
          });

          // Check if lunch break is already covered by prescheduled inspections
          let shouldAddLunchBreak = false;
          try {
            shouldAddLunchBreak = !isLunchBreakAlreadyCovered(currentInspections);
          } catch (error) {
            shouldAddLunchBreak = false
          }
          

          // Create and optimize the route
          routeParams = {
            startAddress: {
              address: inspectorAddress,
              coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
            },
            stopList,
            destinationAddress: {
              address: inspectorAddress,
              coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
            },
            travelMethod: "d",
            lang: "en",
            startTime: startTime,
            endTime: endTime,
            addLunchBreak: shouldAddLunchBreak
          };

          try {
            console.log('PARAMS:', routeParams);
            prescheduledOptimizedRoute = await optimizeInspectorRoutesPrescheduled(routeParams);
            console.log('PRESCHEDULED OPTIMIZED ROUTE:', prescheduledOptimizedRoute);
          } catch (error) {
            console.error(`Error optimizing prescheduled route`, error);
            console.log("Here were the route params:", routeParams);
          }

          if (!prescheduledOptimizedRoute) {
            console.warn(`Missing optimized route data for assignment at index ${i}`);
            failedAssignments.push({
              inspector: assignedInspector,
              inspections: originalInspections,
              reason: 'Manually scheduled inspections infeasible',
              params: routeParams || null,
              selectedDate: selectedDate
            });
            break;
          }

          // Check if any shipments were skipped
          const skippedShipments = prescheduledOptimizedRoute.fullApiResponse[0].skippedShipments || [];
          
          if (skippedShipments.length === 0) {
            // No shipments were skipped, we have a valid route
            break;
          }

          // Some shipments were skipped - we need to remove non-prescheduled inspections
          console.log(`🔍 ${skippedShipments.length} shipments were skipped, removing lowest priority non-prescheduled inspection`);

          // Find the lowest priority non-prescheduled inspection to remove
          const lowestPriorityIndex = findLowestPriorityNonPrescheduledIndex(currentInspections);
          
          if (lowestPriorityIndex === -1) {
            // No non-prescheduled inspections left to remove
            console.log("🔍 Only prescheduled inspections remain - accepting route with skipped shipments");
            break;
          }

          // Remove the lowest priority non-prescheduled inspection
          const removedInspection = currentInspections[lowestPriorityIndex];
          console.warn(`Removing non-prescheduled inspection ${removedInspection.INSPECTION_ID} at ${removedInspection.Street} with rank ${removedInspection.rank}`);
          
          currentInspections = currentInspections.filter((_, index) => index !== lowestPriorityIndex);
          
        } catch (error) {
          console.error(`Error processing prescheduled route for inspector ${assignedInspector.INSPECTOR_ID}:`, error);
          break;
        }
      }

      if (prescheduledOptimizedRoute) {
        // Process the optimized route
        let reorderedInspections = [];
        let visitsData = prescheduledOptimizedRoute.fullApiResponse[0].routes[0].visits;
        let shipmentsIndexes = [];
        for (let i = 0; i < visitsData.length; i++) {
          shipmentsIndexes.push(visitsData[i].shipmentIndex);
        }
        for (let i = 0; i < shipmentsIndexes.length; i++) {
          reorderedInspections.push(currentInspections[shipmentsIndexes[i]]);
        }
        console.log("🔍 Reordered inspections (prescheduled):", reorderedInspections);

        for (let i = 0; i < reorderedInspections.length; i++) {
          let currStartTime = prescheduledOptimizedRoute.routeOrder[i+1].startTime;
          reorderedInspections[i].newAppointmentStart = currStartTime;
          
          // Parse the start time keeping the same timezone
          let [datePart, timePart] = currStartTime.split(' ');
          let [hours, minutes, seconds] = timePart.split(':').map(Number);
          
          // Add duration in seconds
          let totalSeconds = hours * 3600 + minutes * 60 + seconds;
          // add duration for scheduled inspections
          if (reorderedInspections[i].Status === "Scheduled") {
            let duration = parseInt(reorderedInspections[i].DURATION, 10);
            if (!duration) {
              const startTime = new Date(reorderedInspections[i].AppointmentStart);
              const endTime = new Date(reorderedInspections[i].AppointmentFinish);
              duration = Math.round((endTime - startTime) / 1000); // Convert ms to seconds and round
            }
            totalSeconds = totalSeconds + duration;
          } else {
            totalSeconds = totalSeconds + reorderedInspections[i].importedDuration;
          }
          let newHours = Math.floor(totalSeconds / 3600);
          let newMinutes = Math.floor((totalSeconds % 3600) / 60);
          let newSeconds = totalSeconds % 60;
          
          // Format the new time
          let endTime = `${datePart} ${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}:${String(newSeconds).padStart(2, '0')}`;
          reorderedInspections[i].newAppointmentFinish = endTime;
        }

        // Create the optimized assignment object
        const optimizedAssignment = {
          assignedInspector,
          assignedInspections: reorderedInspections,
          curatedLinks: prescheduledOptimizedRoute.curatedLinks,
          legDistances: prescheduledOptimizedRoute.legDistances,
          legTimes: prescheduledOptimizedRoute.legTimes,
          routeOrder: prescheduledOptimizedRoute.routeOrder,
          splitUrls: prescheduledOptimizedRoute.splitUrls,
          totalDistance: prescheduledOptimizedRoute.totalDistance,
          totalTime: prescheduledOptimizedRoute.totalTime,
          breakTime: prescheduledOptimizedRoute.breakTime
        };

        optimizedAssignments.push(optimizedAssignment);
      } else {
        console.warn(`Missing optimized route data for assignment at index ${i}`);
        failedAssignments.push({
          inspector: assignedInspector,
          inspections: originalInspections,
          reason: 'Manually scheduled inspections infeasible',
          params: routeParams || null,
          selectedDate: selectedDate
        });
      }

      // Skip loop iteration to the next assignment
      continue;
    }

    /* 
     * Inspections below do not contain prescheduled inspections
    */

    // Create a working copy of inspections that we can modify
    let currentInspections = [...originalInspections];
    let optimizedRoute = null;
    let routeParams = null; // Declare at higher scope for failedAssignments
    
    // Keep trying until we get a valid route or run out of inspections
    while (currentInspections.length > 0) {
      try {
        console.log(`➡️ Optimizing route ${i+1} of ${assignments.length}`);
        
        // Report progress for each iteration (in case of retries)
        if (onProgress) {
          onProgress(i + 1, assignments.length, assignedInspector.INSPECTOR_ID);
        }

        // Create the inspector's address string
        const inspectorAddress = formatAddress(
          assignedInspector.Street,
          assignedInspector.City,
          assignedInspector.State,
          assignedInspector.Postcode
        );

        // Create the stop list from assigned inspections
        const stopList = currentInspections.map(inspection => {
          return {
            address: formatAddress(
              inspection.Street,
              inspection.City,
              inspection.State,
              inspection.Postcode
            ),
            coordinates: [inspection.lat, inspection.lng]
          };
        });

        let inspectorCoordinates = await getCoordinates(inspectorAddress);

        // Create and optimize the route immediately
        routeParams = {
          startAddress: {
            address: inspectorAddress,
            coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
          },
          stopList,
          destinationAddress: {
            address: inspectorAddress,
            coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
          },
          travelMethod: "d",
          lang: "en"
        };

        // Optimize the route
        optimizedRoute = await optimizeRouteWithAPI(routeParams);
        
        if (!optimizedRoute) {
          console.warn(`Missing optimized route data for assignment at index ${i}`);
          failedAssignments.push({
            inspector: assignedInspector,
            inspections: originalInspections,
            reason: 'Route optimization failed',
            params: routeParams,
            selectedDate: selectedDate
          });
          continue;
        }

        // Extract the waypoint order from the fullApiResponse
        const waypointOrder = optimizedRoute.fullApiResponse.routes[0].waypoint_order;
        
        // Reorder the assignedInspections based on the waypoint order
        let reorderedInspections = [];
        for (let j = 0; j < waypointOrder.length; j++) {
          reorderedInspections.push(currentInspections[waypointOrder[j]]);
        }

        // Sort same-address inspections by priority
        reorderedInspections = sortSameAddressInspections(reorderedInspections);

        // Create the optimized assignment object
        const optimizedAssignment = {
          assignedInspector,
          assignedInspections: reorderedInspections,
          curatedLinks: optimizedRoute.curatedLinks,
          legDistances: optimizedRoute.legDistances,
          legTimes: optimizedRoute.legTimes,
          routeOrder: optimizedRoute.routeOrder,
          splitUrls: optimizedRoute.splitUrls,
          totalDistance: optimizedRoute.totalDistance,
          totalTime: optimizedRoute.totalTime
        };

        // Calculate ETAs
        optimizedRoute = calculateETAs(optimizedAssignment, selectedDate);  // will add breakTime to optimizedRoute too
        
        // Check if route exceeds time limit
        if (!routeExceedsTimeLimit(optimizedRoute)) {
          // Valid route found
          break;
        }
        
        // Route exceeds time limit, remove lowest priority inspection
        const lowestPriorityIndex = findLowestPriorityIndex(currentInspections);
        if (lowestPriorityIndex === -1) break;
        
        console.warn(`Route for inspector ${assignedInspector.INSPECTOR_ID} exceeds 16:30 time limit. Removing inspection at ${currentInspections[lowestPriorityIndex].Street} with rank ${currentInspections[lowestPriorityIndex].rank}`);
        
        currentInspections = currentInspections.filter((_, index) => index !== lowestPriorityIndex);
        
      } catch (error) {
        console.error(`Error processing route for inspector ${assignedInspector.INSPECTOR_ID}:`, error);
        optimizedRoute = null;
        break;
      }
    }
    
    if (optimizedRoute) {
      optimizedAssignments.push(optimizedRoute);
    } else {
      failedAssignments.push({
        inspector: assignedInspector,
        inspections: originalInspections,
        reason: 'Route optimization failed',
        params: routeParams || null,
        selectedDate: selectedDate
      });
    }
  }

  return { optimizedAssignments, failedAssignments };
}

/**
 * Optimizes the route for a single inspector with prescheduled inspections
 * 
 * @param {Object} inspector - Inspector object containing assignedInspections
 * @param {Array} prescheduledInspections - Array of prescheduled inspections
**/
async function optimizeInspectorRoutesPrescheduled(routeParam) {
  if (!routeParam) {
    console.warn('No route parameter provided to optimizeInspectorRoutesPrescheduled');
    return null;
  }
  try {
      const response = await Axios.post(
          `https://api.routora.com/test-google-fleet-api/${process.env.REACT_APP_HASHED_GOOGLE_MAPS_API}`,
          routeParam
      );
      
      return response.data;
  } catch (error) {
      console.error(`Error optimizing route`, error);
      throw error; // Re-throw to handle in calling component
  }
}


/**
 * Calls the route optimization API for a single route
 * 
 * @param {Object} routeParam - Single route parameter to optimize
 * @returns {Promise<Object>} - Optimized route data
 */
async function optimizeRouteWithAPI(routeParam) {
    if (!routeParam) {
        console.warn('No route parameter provided to optimizeRoutesWithAPI');
        return null;
    }

    try {
        const response = await Axios.post(
            `https://api.routora.com/optimize-app-test/${process.env.REACT_APP_HASHED_GOOGLE_MAPS_API}`,
            routeParam
        );
        
        return response.data;
    } catch (error) {
        console.error(`Error optimizing route`, error);
        throw error; // Re-throw to handle in calling component
    }
}

/**
 * Sorts inspections at the same address by priority while maintaining their relative order
 * 
 * @param {Array} inspections - Array of inspections to sort
 * @returns {Array} - Sorted array of inspections with same-address groups ordered by priority
 */
function sortSameAddressInspections(inspections) {
  const sortedInspections = [...inspections];
  
  for (let i = 0; i < sortedInspections.length - 1; i++) {
      // Find all consecutive inspections at the same address
      let sameAddressGroup = [sortedInspections[i]];
      let j = i + 1;
      
      while (j < sortedInspections.length && 
             sortedInspections[j].Street === sortedInspections[i].Street &&
             sortedInspections[j].City === sortedInspections[i].City) {
          sameAddressGroup.push(sortedInspections[j]);
          j++;
      }

      // If we found multiple inspections at the same address
      if (sameAddressGroup.length > 1) {
          // Sort the group by rank (ascending = higher priority first)
          sameAddressGroup.sort((a, b) => a.rank - b.rank);
          
          // Replace the original entries with the sorted group
          for (let k = 0; k < sameAddressGroup.length; k++) {
              sortedInspections[i + k] = sameAddressGroup[k];
          }
          
          // Skip ahead past this group
          i = j - 1;
      }
  }
  
  return sortedInspections;
}

/**
 * Formats an address string from its components
 * 
 * @param {string} street - Street address
 * @param {string} city - City name
 * @param {string} state - State code
 * @param {string} postcode - Postal code
 * @returns {string} - Formatted address string
 */
function formatAddress(street, city, state, postcode) {
  return `${street} ${city} ${state} ${postcode}`.trim();
}

async function getCoordinates(address) {
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      return { lat, lng }
    } catch (error) {
      console.log("😱 Error: ", error);
    }
};

/**
 * Checks if any inspection in the route ends after 16:30
 * 
 * @param {Object} optimizedRoute - Route with ETAs calculated
 * @returns {boolean} - True if any inspection ends after 16:30
 */
function routeExceedsTimeLimit(optimizedRoute) {
    if (!optimizedRoute?.assignedInspections?.length) return false;
    
    // Get the last inspection's finish time
    const lastInspection = optimizedRoute.assignedInspections[optimizedRoute.assignedInspections.length - 1];
    const finishTime = new Date(`2000-01-01 ${lastInspection.newAppointmentFinish.split(' ')[1]}`);
    const timeLimit = new Date(`2000-01-01 16:30:00`);
    
    return finishTime > timeLimit;
}

/**
 * Finds the inspection with the lowest priority (highest rank number) in the list
 * that doesn't share an address with any other inspection and isn't prescheduled.
 * If all inspections share addresses or are prescheduled, returns the lowest priority one overall.
 * 
 * @param {Array} inspections - Array of inspections
 * @returns {number} - Index of the inspection with lowest priority
 */
function findLowestPriorityIndex(inspections) {
    // Helper function to check if an inspection is prescheduled
    const isPrescheduled = (inspection) => {
        return inspection.Status === "Scheduled";
    };

    // Check if all inspections are prescheduled
    const allPrescheduled = inspections.every(inspection => isPrescheduled(inspection));
    
    // First, find all inspections that don't share an address with any other inspection
    // and aren't prescheduled (unless all are prescheduled)
    const uniqueAddressInspections = inspections.filter((inspection, index) => {
        // Skip prescheduled inspections unless all are prescheduled
        if (!allPrescheduled && isPrescheduled(inspection)) {
            return false;
        }
        
        // Check if this inspection's address appears anywhere else in the array
        return !inspections.some((otherInspection, otherIndex) => 
            otherIndex !== index && 
            otherInspection.Street === inspection.Street &&
            otherInspection.City === inspection.City
        );
    });

    // If we found inspections with unique addresses, find the lowest priority one
    if (uniqueAddressInspections.length > 0) {
        let maxRank = -1;
        let maxRankIndex = -1;
        
        uniqueAddressInspections.forEach((inspection) => {
            const index = inspections.indexOf(inspection);
            if (inspection.rank > maxRank) {
                maxRank = inspection.rank;
                maxRankIndex = index;
            }
        });
        
        return maxRankIndex;
    }
    
    // If all inspections share addresses or are prescheduled, find the lowest priority 
    // non-prescheduled one (unless all are prescheduled)
    let maxRank = -1;
    let maxRankIndex = -1;
    
    inspections.forEach((inspection, index) => {
        // Skip prescheduled inspections unless all are prescheduled
        if (!allPrescheduled && isPrescheduled(inspection)) {
            return;
        }
        
        if (inspection.rank > maxRank) {
            maxRank = inspection.rank;
            maxRankIndex = index;
        }
    });
    
    return maxRankIndex;
}

/**
 * Finds the inspection with the lowest priority (highest rank number) that is NOT prescheduled.
 * This function is specifically for prescheduled route optimization where we want to remove
 * non-prescheduled inspections to make room for prescheduled ones.
 * 
 * @param {Array} inspections - Array of inspections
 * @returns {number} - Index of the inspection with lowest priority, or -1 if all are prescheduled
 */
function findLowestPriorityNonPrescheduledIndex(inspections) {
    // Helper function to check if an inspection is prescheduled
    const isPrescheduled = (inspection) => {
        return inspection.Status === "Scheduled";
    };

    // Find all non-prescheduled inspections
    const nonPrescheduledInspections = inspections.filter(inspection => !isPrescheduled(inspection));
    
    if (nonPrescheduledInspections.length === 0) {
        // All inspections are prescheduled
        return -1;
    }
    
    // Find the lowest priority non-prescheduled inspection
    let maxRank = -1;
    let maxRankIndex = -1;
    
    inspections.forEach((inspection, index) => {
        // Only consider non-prescheduled inspections
        if (!isPrescheduled(inspection)) {
            if (inspection.rank > maxRank) {
                maxRank = inspection.rank;
                maxRankIndex = index;
            }
        }
    });
    
    return maxRankIndex;
}



// Old code for reference

// import {getGeocode, getLatLng,} from "use-places-autocomplete";
// import { calculateETAs } from '../calculations/calculateETAs';
// import Axios from 'axios'

// /**
//  * optimizeInspections.js
//  * 
//  * This file contains functions to transform inspection assignments into a format
//  * suitable for route optimization. It takes the assignments data from SelectionDashboard.js
//  * and converts it into a format that can be used with a route optimization API.
//  */

// /**
//  * Transforms the assignments data into a format suitable for route optimization
//  * 
//  * @param {Array} assignments - Array of objects containing assignedInspector and assignedInspections
//  * @returns {Promise<Array>} - Array of optimized assignments with route information
//  */
// export async function optimizeInspectorRoutes(assignments, selectedDate) {
//   if (!assignments || !Array.isArray(assignments)) {
//     console.warn('Invalid assignments data provided to optimizeInspectorRoutes');
//     return [];
//   }

//   const optimizedAssignments = [];
  
//   // Process each assignment and optimize immediately
//   for (let i = 0; i < assignments.length; i++) {
//     const { assignedInspector, assignedInspections } = assignments[i];
    
//     // Skip if no inspections assigned
//     if (!assignedInspections || assignedInspections.length === 0) {
//       continue;
//     }

//     console.log(`Optimizing route ${i+1} of ${assignments.length}`);

//     // Create the inspector's address string
//     const inspectorAddress = formatAddress(
//       assignedInspector.Street,
//       assignedInspector.City,
//       assignedInspector.State,
//       assignedInspector.Postcode
//     );

//     // Create the stop list from assigned inspections
//     const stopList = assignedInspections.map(inspection => {
//       return {
//         address: formatAddress(
//           inspection.Street,
//           inspection.City,
//           inspection.State,
//           inspection.Postcode
//         ),
//         coordinates: [inspection.lat, inspection.lng]
//       };
//     });

//     let inspectorCoordinates = await getCoordinates(inspectorAddress);

//     // Create and optimize the route immediately
//     const routeParams = {
//       startAddress: {
//         address: inspectorAddress,
//         coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
//       },
//       stopList,
//       destinationAddress: {
//         address: inspectorAddress,
//         coordinates: [inspectorCoordinates.lat, inspectorCoordinates.lng]
//       },
//       travelMethod: "d",
//       lang: "en"
//     };

//     // Optimize the route
//     let optimizedRoute = await optimizeRouteWithAPI(routeParams);
    
//     if (!optimizedRoute) {
//       console.warn(`Missing optimized route data for assignment at index ${i}`);
//       continue;
//     }

//     // Extract the waypoint order from the fullApiResponse
//     const waypointOrder = optimizedRoute.fullApiResponse.routes[0].waypoint_order;
    
//     // Reorder the assignedInspections based on the waypoint order
//     let reorderedInspections = [];
//     for (let j = 0; j < waypointOrder.length; j++) {
//         reorderedInspections.push(assignedInspections[waypointOrder[j]]);
//     }

//     // Sort same-address inspections by priority
//     reorderedInspections = sortSameAddressInspections(reorderedInspections);

//     // Create the optimized assignment object
//     const optimizedAssignment = {
//       assignedInspector: assignedInspector,
//       assignedInspections: reorderedInspections,
//       curatedLinks: optimizedRoute.curatedLinks,
//       legDistances: optimizedRoute.legDistances,
//       legTimes: optimizedRoute.legTimes,
//       routeOrder: optimizedRoute.routeOrder,
//       splitUrls: optimizedRoute.splitUrls,
//       totalDistance: optimizedRoute.totalDistance,
//       totalTime: optimizedRoute.totalTime
//     };

//     const optimizedRouteWithETAs = calculateETAs(optimizedAssignment, selectedDate);
//     optimizedAssignments.push(optimizedRouteWithETAs);
//   }

//   return optimizedAssignments;
// }

// /**
//  * Calls the route optimization API for a single route
//  * 
//  * @param {Object} routeParam - Single route parameter to optimize
//  * @returns {Promise<Object>} - Optimized route data
//  */
// async function optimizeRouteWithAPI(routeParam) {
//     if (!routeParam) {
//         console.warn('No route parameter provided to optimizeRoutesWithAPI');
//         return null;
//     }

//     try {
//         const response = await Axios.post(
//             `https://api.routora.com/optimize-app-test/${process.env.REACT_APP_HASHED_GOOGLE_MAPS_API}`,
//             routeParam
//         );
        
//         return response.data;
//     } catch (error) {
//         console.error(`Error optimizing route`, error);
//         throw error; // Re-throw to handle in calling component
//     }
// }

// /**
//  * Sorts inspections at the same address by priority while maintaining their relative order
//  * 
//  * @param {Array} inspections - Array of inspections to sort
//  * @returns {Array} - Sorted array of inspections with same-address groups ordered by priority
//  */
// function sortSameAddressInspections(inspections) {
//   const sortedInspections = [...inspections];
  
//   for (let i = 0; i < sortedInspections.length - 1; i++) {
//       // Find all consecutive inspections at the same address
//       let sameAddressGroup = [sortedInspections[i]];
//       let j = i + 1;
      
//       while (j < sortedInspections.length && 
//              sortedInspections[j].Street === sortedInspections[i].Street &&
//              sortedInspections[j].City === sortedInspections[i].City) {
//           sameAddressGroup.push(sortedInspections[j]);
//           j++;
//       }

//       // If we found multiple inspections at the same address
//       if (sameAddressGroup.length > 1) {
//           // Sort the group by rank (ascending = higher priority first)
//           sameAddressGroup.sort((a, b) => a.rank - b.rank);
          
//           // Replace the original entries with the sorted group
//           for (let k = 0; k < sameAddressGroup.length; k++) {
//               sortedInspections[i + k] = sameAddressGroup[k];
//           }
          
//           // Skip ahead past this group
//           i = j - 1;
//       }
//   }
  
//   return sortedInspections;
// }

// /**
//  * Formats an address string from its components
//  * 
//  * @param {string} street - Street address
//  * @param {string} city - City name
//  * @param {string} state - State code
//  * @param {string} postcode - Postal code
//  * @returns {string} - Formatted address string
//  */
// function formatAddress(street, city, state, postcode) {
//   return `${street} ${city} ${state} ${postcode}`.trim();
// }

// async function getCoordinates(address) {
//     try {
//       const results = await getGeocode({ address });
//       const { lat, lng } = await getLatLng(results[0]);
//       return { lat, lng }
//     //   panTo({ lat, lng });
//     } catch (error) {
//       console.log("😱 Error: ", error);
//     }
// };
